import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { AlertTriangle, Info } from 'lucide-react'
import { Button } from '@components/ui/button'
import { applyQuoteEstimateChange, dismissQuoteEstimateChange, getQuoteEstimateChange } from '../../api/api'
import { Logger } from '../../logger'

const itemLabel = (item) => `${item.name || `#${item.product_id ?? item.productId}`}${item.count > 1 ? ` ×${item.count}` : ''}`

const ItemList = ({ title, items }) =>
  items?.length ? (
    <p className='mt-1 text-[13px]'>
      <span className='font-semibold'>{title}: </span>
      {items.map(itemLabel).join(', ')}
    </p>
  ) : null

ItemList.propTypes = { title: PropTypes.string.isRequired, items: PropTypes.array }

const versions = (change) =>
  change.from_version && change.to_version ? ` (estimate version ${change.from_version} → ${change.to_version})` : ''

/** What a newer version of the vehicle's estimate did, or would do, to this quote (QuoteEstimateRefreshService). */
export const EstimateChangeBanner = ({ quoteId, readOnly, onApplied }) => {
  const [change, setChange] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!quoteId) return
    try {
      const response = await getQuoteEstimateChange(quoteId)
      setChange(response?.status === 200 ? response.data : null)
    } catch (e) {
      setChange(null)
    }
  }, [quoteId])

  useEffect(() => {
    load()
  }, [load])

  if (!change) return null

  const act = async (action) => {
    setBusy(true)
    setError(null)
    try {
      if (action === 'apply') {
        await applyQuoteEstimateChange(quoteId, change.id)
        onApplied?.()
      } else {
        await dismissQuoteEstimateChange(quoteId, change.id)
      }
      setChange(null)
    } catch (e) {
      Logger.error({ message: e, payload: { file: 'EstimateChangeBanner', action, quoteId, changeId: change.id } })
      setError(e?.response?.data?.message || 'Could not update the quote. Please try again.')
      load()
    } finally {
      setBusy(false)
    }
  }

  const review = change.status !== 'APPLIED' || change.kept?.length > 0
  let heading
  if (change.status === 'PENDING') {
    heading = `The estimate for this vehicle changed${versions(change)}. Update the quote to match?`
  } else if (change.status === 'FLAGGED' && change.reason === 'VIN_CHANGED') {
    // Same RO, VIN a few characters off: a corrected (or mistyped) VIN. Staff decide which is right.
    const vin = change.estimate_vin ? ` (${change.estimate_vin})` : ''
    heading = `The latest estimate has a different VIN${vin} than this quote's vehicle, so this quote was not updated. Please check the VIN.`
  } else if (change.status === 'FLAGGED') {
    const what = change.reason === 'INSURER_CHANGED' ? 'insurance company' : 'client'
    heading = `The latest estimate names a different ${what}, so this quote was not updated. Please review it.`
  } else {
    heading = `This quote was updated from the latest estimate${versions(change)}.`
  }

  return (
    <div
      role='status'
      data-testid='estimate-change-banner'
      className={`mb-4 flex items-start gap-3 rounded-lg border p-4 ${
        review ? 'border-[#F59E0B] bg-[#FFFBEB] text-[#78350F]' : 'border-[#BFDBFE] bg-[#EFF6FF] text-[#1E3A8A]'
      }`}
    >
      {review ? (
        <AlertTriangle className='mt-0.5 h-5 w-5 shrink-0' aria-hidden='true' />
      ) : (
        <Info className='mt-0.5 h-5 w-5 shrink-0' aria-hidden='true' />
      )}
      <div className='min-w-0 flex-1'>
        <p className='text-[14px] font-semibold'>{heading}</p>
        <ItemList title={change.status === 'PENDING' ? 'Will add' : 'Added'} items={change.added} />
        <ItemList title={change.status === 'PENDING' ? 'Will remove' : 'Removed'} items={change.removed} />
        <ItemList title='Kept for review (no longer in the estimate, but edited on this quote)' items={change.kept} />
        {error && <p className='mt-2 text-[13px] text-[#B91C1C]'>{error}</p>}
      </div>
      {!readOnly && (
        <div className='flex shrink-0 gap-2'>
          {change.can_apply && (
            <Button size='sm' disabled={busy} onClick={() => act('apply')}>
              Update quote
            </Button>
          )}
          <Button size='sm' variant='outline' disabled={busy} onClick={() => act('dismiss')}>
            Dismiss
          </Button>
        </div>
      )}
    </div>
  )
}

EstimateChangeBanner.propTypes = {
  quoteId: PropTypes.number,
  readOnly: PropTypes.bool,
  onApplied: PropTypes.func
}
